import {
  TimeSheetsListingGetters,
  TimeSheetsListingActions,
  TimeSheetsListingMutations
} from '@/app/features/time-sheets/features/time-sheets-listing/time-sheets-listing.keys';
import { timeSheetsListingService as timeSheetsService } from '@/app/features/time-sheets/features/time-sheets-listing/time-sheets-listing.service';
import { TolErrorsActions } from '@/app/features/tol-errors/tol-errors.keys';
import i18n from '@/i18n';

export const getInitialState = () => {
  return {
    timeSheetsBulkAction: null,
    batchTimeSheetsList: [],
    timeSheetsAssignments: [],
    timeSheetsContractors: [],
    timeSheetsFilteredSortedRecordCount: 0,
    timeSheetsSearchParam: '',
    timeSheetsFilters: {
      contractorId: 0,
      assignmentId: 0,
      dateRangeFilters: {
        startDate: null,
        endDate: null
      },
      statusFilters: {
        draft: false,
        submitted: false,
        approved: false,
        processed: false,
        rejected: false,
        obsolete: false
      }
    },
    timeSheetsSortOrder: {
      sortBy: 'startDate',
      sortDirection: 'desc'
    },
    timeSheetsIsLoading: false,
    timeSheets: [],
    modifiedTimeSheets: {},
    timeSheetsStatusCounts: {
      draft: 0,
      submitted: 0,
      approved: 0,
      processed: 0,
      rejected: 0,
      obsolete: 0
    },
    timeSheetsPaginationParameters: {
      recordsPerPage: 10,
      offset: 0
    }
  };
};

export const timeSheetsListingStore = {
  state: getInitialState(),
  getters: {
    [TimeSheetsListingGetters.SelectedTimeSheets]: state => param => {
      const selectedTimeSheets = state.timeSheets.filter(sheet => sheet.isSelected === true);
      return selectedTimeSheets.map(sheet => sheet[param]);
    },
    [TimeSheetsListingGetters.TimeSheetsSearchParam]: state => {
      return state.timeSheetsSearchParam;
    },
    [TimeSheetsListingGetters.ModifiedTimeSheets]: state => {
      return state.modifiedTimeSheets;
    },
    [TimeSheetsListingGetters.TimeSheetsPerPage]: state => {
      return state.timeSheetsPaginationParameters.recordsPerPage;
    },
    [TimeSheetsListingGetters.TimeSheetsListingOffset]: state => {
      return state.timeSheetsPaginationParameters.offset;
    },
    [TimeSheetsListingGetters.TimeSheetsFilteredSortedRecordCount]: state => {
      return state.timeSheetsFilteredSortedRecordCount;
    },
    [TimeSheetsListingGetters.TimesheetContractors]: state => {
      return state.timeSheetsContractors;
    },
    [TimeSheetsListingGetters.TimeSheetIsLoading]: state => {
      return state.timeSheetsIsLoading;
    },
    [TimeSheetsListingGetters.GetTimeSheetsSortOrder]: state => {
      return state.timeSheetsSortOrder;
    },
    [TimeSheetsListingGetters.GetBatchTimeSheetsList]: state => {
      return state.batchTimeSheetsList;
    }
  },
  mutations: {
    [TimeSheetsListingMutations.SetTimeSheetsSearchParam]: function(state, searchParam) {
      state.timeSheetsSearchParam = searchParam;
    },
    [TimeSheetsListingMutations.SetTimeSheetsFilters]: function(state, filters) {
      state.timeSheetsFilters = filters;
    },
    [TimeSheetsListingMutations.SetTimeSheetsSortOrder]: function(state, sortOrder) {
      state.timeSheetsSortOrder = sortOrder;
    },
    [TimeSheetsListingMutations.SetTimeSheetsLoading]: function(state, isLoading = true) {
      state.timeSheetsIsLoading = isLoading;
    },
    [TimeSheetsListingMutations.RetrieveTimeSheetsAssignmentsSuccess]: function(
      state,
      assignments
    ) {
      //state.timeSheetsIsLoading = false;
      state.timeSheetsAssignments = assignments;
    },
    [TimeSheetsListingMutations.RetrieveTimeSheetsContractorsSuccess]: function(
      state,
      contractors
    ) {
      //state.timeSheetsIsLoading = false;
      state.timeSheetsContractors = contractors;
    },
    [TimeSheetsListingMutations.RetrieveTimeSheetsSuccess]: function(state, timeSheets) {
      //add 'isSelected' field to timeSheet object to support bulk actions
      const modifiedTimeSheets = timeSheets.map(sheet => {
        return { ...sheet, isSelected: false };
      });

      state.timeSheets = modifiedTimeSheets;
      //state.timeSheetsIsLoading = false;
    },
    [TimeSheetsListingMutations.RetrieveTimeSheetsStatusCountsSuccess]: function(
      state,
      timeSheetsStatusCounts
    ) {
      //state.timeSheetsIsLoading = false;
      state.timeSheetsStatusCounts = timeSheetsStatusCounts;
    },
    [TimeSheetsListingMutations.SetTimeSheetsBulkActionValue]: (state, bulkAction) => {
      state.timeSheetsBulkAction = bulkAction;
    },
    [TimeSheetsListingMutations.UpdateSelectedTimeSheets]: (state, payload) => {
      if (typeof payload === 'string') {
        const idx = state.timeSheets.findIndex(sheet => sheet.referenceNumber === payload);
        const selectedTimeSheet = state.timeSheets[idx];

        //toggle the 'isSelected' field of chosen timeSheet
        selectedTimeSheet.isSelected = !selectedTimeSheet.isSelected;

        //modify state
        state.timeSheets[idx] = selectedTimeSheet;
      } else if (typeof payload === 'object') {
        state.timeSheets.forEach(sheet => (sheet.isSelected = payload.checked));
      }
    },
    [TimeSheetsListingMutations.SetTimeSheetsModifiedWithBulkAction]: (state, referenceNumbers) => {
      const toUpdate = Object.assign({}, state.modifiedTimeSheets);

      //Create a map of reference numbers of modified items
      //This will be used to lookup item reference numbers to determine
      //if 'is-modified' class shoud be attached to checkboxes in underlying Table component
      const refMap = referenceNumbers.reduce((obj, referenceNumber, index) => {
        obj[referenceNumber] = index;
        return obj;
      }, {});

      state.modifiedTimeSheets = { ...toUpdate, ...refMap };
    },
    [TimeSheetsListingMutations.ResetTimeSheetsState]: state => {
      Object.assign(state, getInitialState());
    },
    [TimeSheetsListingMutations.UpdateTimeSheetsPaginationParameters]: (state, params) => {
      state.timeSheetsPaginationParameters.recordsPerPage = params.recordsPerPage;
      state.timeSheetsPaginationParameters.offset = params.offset;
    },
    [TimeSheetsListingMutations.ResetTimeSheetsPaginationOffset]: state => {
      state.timeSheetsPaginationParameters.offset = 0;
    },
    [TimeSheetsListingMutations.SetTimeSheetsFilteredSortedRecordCount]: (state, recordCount) => {
      state.timeSheetsFilteredSortedRecordCount = recordCount.count;
    },
    [TimeSheetsListingMutations.SetBatchTimeSheetsList]: (state, payload) => {
      state.batchTimeSheetsList = payload;
    }
  },
  actions: {
    [TimeSheetsListingActions.SetTimeSheetsFilters]: function({ commit, dispatch }, filters) {
      commit(TimeSheetsListingMutations.SetTimeSheetsFilters, filters);
      commit(TimeSheetsListingMutations.ResetTimeSheetsPaginationOffset);
      dispatch(TimeSheetsListingActions.RetrieveTimeSheets);
    },

    [TimeSheetsListingActions.SetTimeSheetsSortOrder]: function({ commit, dispatch }, sortOrder) {
      commit(TimeSheetsListingMutations.SetTimeSheetsSortOrder, sortOrder);
      commit(TimeSheetsListingMutations.ResetTimeSheetsPaginationOffset);
      dispatch(TimeSheetsListingActions.RetrieveTimeSheets);
    },
    [TimeSheetsListingActions.RetrieveTimeSheetsAssignments]: function({ commit, dispatch }) {
      commit(TimeSheetsListingMutations.SetTimeSheetsLoading);

      return timeSheetsService
        .retrieveAssignments()
        .then(assignments =>
          commit(TimeSheetsListingMutations.RetrieveTimeSheetsAssignmentsSuccess, assignments)
        )
        .catch(error => {
          dispatch(TolErrorsActions.SetError, {
            error,
            message: i18n.t('timeSheets.listing.errors.retrieveAssignments')
          });
        });
    },
    [TimeSheetsListingActions.RetrieveTimeSheetsContractors]: function({ commit, dispatch }) {
      commit(TimeSheetsListingMutations.SetTimeSheetsLoading);

      return timeSheetsService
        .retrieveContractors()
        .then(contractors =>
          commit(TimeSheetsListingMutations.RetrieveTimeSheetsContractorsSuccess, contractors)
        )
        .catch(error => {
          dispatch(TolErrorsActions.SetError, {
            error,
            message: i18n.t('timeSheets.listing.errors.retrieveContractors')
          });
        });
    },
    [TimeSheetsListingActions.RetrieveTimeSheets]: function(
      { commit, state, dispatch, getters },
      afterBulkAction = false
    ) {
      commit(TimeSheetsListingMutations.SetTimeSheetsLoading);
      const modifiedTimeSheets = getters.SelectedTimeSheets('referenceNumber');

      if (afterBulkAction)
        commit(TimeSheetsListingMutations.SetTimeSheetsModifiedWithBulkAction, modifiedTimeSheets);

      return timeSheetsService
        .retrieveTimeSheets(
          state.timeSheetsFilters,
          state.timeSheetsSortOrder,
          state.timeSheetsPaginationParameters,
          state.timeSheetsSearchParam
        )
        .then(timeSheets => {
          if (!state.timeSheetsSearchParam) {
            dispatch(TimeSheetsListingActions.RetrieveTimeSheetsFilteredSortedRecordCount);
          }
          commit(TimeSheetsListingMutations.RetrieveTimeSheetsSuccess, timeSheets);
        })
        .then(() => {
          commit(TimeSheetsListingMutations.SetTimeSheetsLoading, false);
        })
        .catch(error => {
          dispatch(TolErrorsActions.SetError, {
            error,
            message: i18n.t('timeSheets.listing.errors.retrieveTimeSheets')
          });
        });
    },
    [TimeSheetsListingActions.RetrieveTimeSheetsStatusCounts]: function({ commit, dispatch }) {
      commit(TimeSheetsListingMutations.SetTimeSheetsLoading);

      return timeSheetsService
        .retrieveStatusCounts()
        .then(timeSheetStatusCounts =>
          commit(
            TimeSheetsListingMutations.RetrieveTimeSheetsStatusCountsSuccess,
            timeSheetStatusCounts
          )
        )
        .catch(error => {
          dispatch(TolErrorsActions.SetError, {
            error,
            message: i18n.t('timeSheets.listing.errors.retrieveStatusCount')
          });
        });
    },
    [TimeSheetsListingActions.ExportTimeSheetsAsExcel]: function({ state }) {
      return timeSheetsService.downloadTimeSheetsXlsx(
        state.timeSheetsFilters,
        state.timeSheetsSortOrder
      );
    },
    [TimeSheetsListingActions.SelectTimeSheet]: ({ commit }, referenceNumber) => {
      commit(TimeSheetsListingMutations.UpdateSelectedTimeSheets, referenceNumber);
    },
    [TimeSheetsListingActions.SelectDeselectAllTimeSheets]: ({ commit }, isSelected) => {
      commit(TimeSheetsListingMutations.UpdateSelectedTimeSheets, isSelected);
    },
    [TimeSheetsListingActions.SetTimeSheetsBulkAction]: ({ commit }, bulkAction) => {
      commit(TimeSheetsListingMutations.SetTimeSheetsBulkActionValue, bulkAction);
    },
    [TimeSheetsListingActions.DoTimeSheetsBulkAction]: (
      { dispatch, commit, getters },
      bulkAction
    ) => {
      const selectedTimeSheetRefs = getters.SelectedTimeSheets('referenceNumber');
      commit(TimeSheetsListingMutations.SetTimeSheetsLoading);
      return timeSheetsService
        .bulkUpdateTimeSheets(bulkAction, selectedTimeSheetRefs)
        .then(response => {
          if (bulkAction.action === 'PDF') {
            commit(TimeSheetsListingMutations.SetBatchTimeSheetsList, response);
          } else {
            dispatch(TimeSheetsListingActions.RetrieveTimeSheets, true);
          }
          commit(TimeSheetsListingMutations.SetTimeSheetsLoading, false);
        })
        .catch(error => {
          dispatch(TolErrorsActions.SetError, {
            error,
            message: i18n.t('timeSheets.listing.errors.bulkApproveRejectFailed')
          });
        });
    },
    [TimeSheetsListingActions.SetInitialTimeSheetsState]: ({ commit }) => {
      commit(TimeSheetsListingMutations.ResetTimeSheetsState);
    },
    [TimeSheetsListingActions.SetTimeSheetsPaginationParameters]: (
      { state, commit, dispatch },
      params
    ) => {
      commit(TimeSheetsListingMutations.UpdateTimeSheetsPaginationParameters, params);
      dispatch(
        TimeSheetsListingActions.RetrieveTimeSheetsSearchRecordCount,
        state.timeSheetsSearchParam
      );
      dispatch(TimeSheetsListingActions.RetrieveTimeSheets);
    },
    [TimeSheetsListingActions.UpdateTimeSheetsSearchParam]: ({ commit, dispatch }, payload) => {
      commit(TimeSheetsListingMutations.SetTimeSheetsSearchParam, payload.searchParam);
      commit(TimeSheetsListingMutations.SetTimeSheetsLoading);
      return dispatch(TimeSheetsListingActions.RetrieveTimeSheetsFilteredSortedRecordCount)
        .then(() => {
          commit(TimeSheetsListingMutations.RetrieveTimeSheetsSuccess, payload.timeSheets);
        })
        .then(() => commit(TimeSheetsListingMutations.SetTimeSheetsLoading, false));
    },
    [TimeSheetsListingActions.RetrieveTimeSheetsFilteredSortedRecordCount]: function({
      commit,
      state,
      dispatch
    }) {
      return timeSheetsService
        .retrieveFilteredSortedRecordCount(
          state.timeSheetsFilters,
          state.timeSheetsSortOrder,
          state.timeSheetsSearchParam
        )
        .then(recordCount => {
          commit(TimeSheetsListingMutations.SetTimeSheetsFilteredSortedRecordCount, recordCount);
        })
        .catch(error => {
          dispatch(TolErrorsActions.SetError, {
            error,
            message: i18n.t('timeSheets.listing.errors.retrieveTimeSheets')
          });
        });
    },
    [TimeSheetsListingActions.RetrieveTimeSheetsSearchRecordCount]: function(
      { commit, state, dispatch },
      searchParam
    ) {
      return timeSheetsService
        .retrieveFilteredSortedRecordCount(
          state.timeSheetsFilters,
          state.timeSheetsSortOrder,
          searchParam
        )
        .then(recordCount => {
          commit(TimeSheetsListingMutations.SetTimeSheetsFilteredSortedRecordCount, recordCount);
        })
        .catch(error => {
          dispatch(TolErrorsActions.SetError, {
            error,
            message: i18n.t('timeSheets.listing.errors.retrieveTimeSheets')
          });
        });
    },
    [TimeSheetsListingActions.BatchTimeSheetslist]: function({ commit }, response) {
      commit(TimeSheetsListingMutations.SetBatchTimeSheetsList, response);
    }
  }
};
